import React, { useState, useEffect, useMemo } from 'react';
import { 
  Trash2, RotateCcw, Search, FileSpreadsheet, RefreshCw, 
  ChevronDown, X, AlertCircle
} from 'lucide-react';
import { crmDataService } from '../services/crmDataService';
import * as XLSX from 'xlsx';
import PermissionGate from './PermissionGate'; // 🚨 [추가] 자물쇠 부품 가져오기

interface LostDealsProps {
  currentUser?: any; 
  onRefresh: () => void;
  
}

// 🚨 매출예정표와 동일한 인터페이스 사용
interface ForecastItem {
  id: string;
  구분: string;
  poNo: string;
  projNo: string;
  거래처: string;
  담당자: string;
  지역: string;
  제품금액: number;
  상품금액: number;
  소계: number;
  매출예정일: string;
  수금예정일: string;
  등급: string;
  건명: string;
  영업리드: string;
  산업분야: string;
  팀: string;
}

// 🚨 [수정] 부모(App.tsx)로부터 받은 currentUser를 받도록 선언 보강
export default function LostDeals({ currentUser, onRefresh }: LostDealsProps) {
  const [lostList, setLostList] = useState<ForecastItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // 테이블 컬럼 너비 (SalesForecast와 동일하게 세팅)
  const colWidths: Record<string, number> = {
    category: 80, poNo: 80, projNo: 80, company: 160, rep: 80, region: 70, 
    prod: 110, item: 110, subtotal: 120, date: 100, collectDate: 100, 
    grade: 60, title: 200, lead: 100, industry: 100, action: 100 
  };

  const gradeColors: Record<string, string> = {
    'S': 'bg-purple-100 text-purple-700 border-purple-200',
    'A': 'bg-blue-100 text-blue-700 border-blue-200',
    'B': 'bg-green-100 text-green-700 border-green-200',
    'C': 'bg-cyan-100 text-cyan-700 border-cyan-200',
    'D': 'bg-slate-100 text-slate-500 border-slate-200'
  };

  const fetchLostData = async () => {
  setLoading(true);
  try {
    // 🚨 4번째 인자에 "lost"를 명시적으로 전달합니다.
    // getSalesForecastList(startDate, endDate, refresh, type)
    const data = await crmDataService.getSalesForecastList(undefined, undefined, false, "lost"); 
    
    // 이제 백엔드에서 이미 '이탈'만 골라서 보내주므로 
    // 프론트에서 .filter()를 또 할 필요가 없습니다!
    setLostList(data); 
    
    console.log("✅ 이탈 거래처 데이터 로드 완료:", data.length, "건");
  } catch (err) { 
    console.error("❌ 이탈 데이터 로드 실패:", err); 
  } finally { 
    setLoading(false); 
  }
};

  useEffect(() => { fetchLostData(); }, []);

  const handleRestore = async (id: string) => {
  // 🚨 사용자에게 원래 어떤 상태였는지 물어봅니다.
  const targetStage = prompt("복구할 단계를 입력하세요 (실적/확정/예정/예산):", "예정");
  
  if (!targetStage || !['실적', '확정', '예정', '예산'].includes(targetStage)) {
    alert("올바른 단계를 입력해야 복구가 가능합니다.");
    return;
  }

  try {
    // 사용자가 입력한 값(targetStage)으로 업데이트!
    await crmDataService.updateForecastItem(id, { 구분: targetStage }, "이탈 거래처 복구", "관리자");
    alert(`[${targetStage}] 상태로 복구되었습니다.`);
    if (onRefresh) onRefresh();
    fetchLostData();
  } catch (err) { alert("복구 실패"); }
};

  const formatKRW = (val: number) => new Intl.NumberFormat('ko-KR').format(Math.round(val || 0));

  // 엑셀 다운로드 (모든 컬럼 포함)
  const handleExportExcel = () => {
    if (!lostList.length) return;
    const ws = XLSX.utils.json_to_sheet(lostList);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Lost_Deals');
    XLSX.writeFile(wb, `lost_deals_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div className="flex flex-col gap-4 pb-20 animate-fade-in font-sans">
      
      {/* 상단 헤더 섹션 */}
      <div className="flex items-center justify-between bg-white p-5 rounded-[24px] shadow-sm border border-slate-200">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-rose-500 text-white rounded-2xl shadow-lg shadow-rose-100"><Trash2 size={24} /></div>
          <div>
            <h1 className="text-xl font-black text-slate-900 tracking-tight">이탈 거래처 분석</h1>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Lost & Dropped Accounts Archive</p>
          </div>
        </div>
        <div className="bg-rose-50 px-6 py-2 rounded-2xl border border-rose-100 flex flex-col items-center">
           <span className="text-[10px] font-black text-rose-400 uppercase">Total Items</span>
           <span className="text-xl font-[1000] text-rose-600 font-mono">{lostList.length}</span>
        </div>
      </div>

      {/* 필터 바 */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 flex gap-4 items-center">
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-4 py-2 rounded-xl flex-1 focus-within:ring-2 focus-within:ring-rose-100 transition-all">
          <Search size={16} className="text-slate-400" />
          <input 
            type="text" placeholder="이탈 거래처명 또는 건명으로 검색..." 
            className="bg-transparent text-sm font-bold outline-none w-full text-slate-700"
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <button onClick={fetchLostData} className="p-2 hover:bg-slate-50 rounded-xl border border-slate-200 transition-all">
            <RefreshCw size={18} className={loading ? "animate-spin text-rose-500" : "text-slate-400"} />
          </button>
          <button onClick={handleExportExcel} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-black hover:bg-emerald-700 shadow-md transition-all">
            <FileSpreadsheet size={16} /> 엑셀 다운로드
          </button>
        </div>
      </div>

      {/* 🚀 리스트 테이블 (SalesForecast 디자인 완벽 복제) */}
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse table-fixed min-w-[1600px]">
            <thead>
              <tr className="bg-[#1e293b] text-slate-200 text-[11px] font-bold uppercase h-12 border-b border-slate-700">
                <th style={{ width: colWidths.category }} className="px-4 text-center border-r border-slate-700/50">구분</th>
                <th style={{ width: colWidths.poNo }} className="px-4 text-center border-r border-slate-700/50">PO.No</th>
                <th style={{ width: colWidths.projNo }} className="px-4 text-center border-r border-slate-700/50">Proj.No</th>
                <th style={{ width: colWidths.company }} className="px-4 border-r border-slate-700/50">거래처</th>
                <th style={{ width: colWidths.rep }} className="px-4 text-center border-r border-slate-700/50">담당자</th>
                <th style={{ width: colWidths.region }} className="px-4 text-center border-r border-slate-700/50">지역</th>
                <th style={{ width: colWidths.prod }} className="px-4 text-center border-r border-slate-700/50">제품금액</th>
                <th style={{ width: colWidths.item }} className="px-4 text-center border-r border-slate-700/50">상품금액</th>
                <th style={{ width: colWidths.subtotal }} className="px-4 text-center border-r border-slate-700/50">소계</th>
                <th style={{ width: colWidths.date }} className="px-4 text-center border-r border-slate-700/50">매출예정일</th>
                <th style={{ width: colWidths.collectDate }} className="px-4 text-center border-r border-slate-700/50">수금예정일</th>
                <th style={{ width: colWidths.grade }} className="px-4 text-center border-r border-slate-700/50">등급</th>
                <th style={{ width: colWidths.title }} className="px-4 border-r border-slate-700/50">건명</th>
                <th style={{ width: colWidths.lead }} className="px-4 text-center border-r border-slate-700/50">영업리드</th>
                <th style={{ width: colWidths.industry }} className="px-4 text-center border-r border-slate-700/50">산업분야</th>
                <th style={{ width: colWidths.action }} className="px-4 text-center">복구관리</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-center">
              {lostList.filter(c => c.거래처.includes(searchTerm) || c.건명.includes(searchTerm)).map((item) => (
                <tr key={item.id} className="text-[12px] h-12 hover:bg-rose-50/30 transition-colors group">
                  <td className="px-4 border-r border-slate-100">
                    <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-rose-500 text-white shadow-sm">이탈</span>
                  </td>
                  <td className="px-4 border-r border-slate-100 font-mono text-slate-400 truncate">{item.poNo}</td>
                  <td className="px-4 border-r border-slate-100 font-mono text-slate-400 truncate">{item.projNo}</td>
                  <td className="px-4 border-r border-slate-100 font-bold text-slate-400 bg-slate-50/50 text-left truncate">{item.거래처}</td>
                  <td className="px-4 border-r border-slate-100 font-black text-slate-500">{item.담당자}</td>
                  <td className="px-4 border-r border-slate-100 text-slate-400 text-center">{item.지역}</td>
                  <td className="px-4 border-r border-slate-100 text-right font-mono text-slate-400">{formatKRW(item.제품금액)}</td>
                  <td className="px-4 border-r border-slate-100 text-right font-mono text-slate-400">{formatKRW(item.상품금액)}</td>
                  <td className="px-4 border-r border-slate-100 text-right font-bold text-slate-500 bg-slate-50/30">{formatKRW(item.소계)}</td>
                  <td className="px-4 border-r border-slate-100 text-center font-mono text-slate-400">{item.매출예정일}</td>
                  <td className="px-4 border-r border-slate-100 text-center font-mono text-slate-400">{item.수금예정일}</td>
                  <td className="px-4 border-r border-slate-100 text-center">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-black border opacity-50 ${gradeColors[item.등급 || 'D']}`}>{item.등급}</span>
                  </td>
                  <td className="px-4 border-r border-slate-100 text-left truncate text-slate-400">{item.건명}</td>
                  <td className="px-4 border-r border-slate-100 text-center font-bold text-slate-400">{item.영업리드}</td>
                  <td className="px-4 border-r border-slate-100 text-center text-slate-400 text-[10px]">{item.산업분야}</td>
                  <td className="px-4 text-center">
                    {/* 🚨 [수정] 복구 버튼 자물쇠 채우기 */}
                    <PermissionGate user={currentUser} action="lost_edit">
                      <button 
                        onClick={() => handleRestore(item.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-[11px] font-black hover:bg-indigo-700 transition-all shadow-md active:scale-95 mx-auto"
                      >
                        <RotateCcw size={12} strokeWidth={3} /> 복구
                      </button>
                    </PermissionGate>
                  </td>
                </tr>
              ))}
              {lostList.length === 0 && (
                <tr>
                  <td colSpan={16} className="py-24 text-center text-slate-300 font-bold italic bg-slate-50/30">
                    <AlertCircle size={48} className="mx-auto mb-4 opacity-10" />
                    현재 파이프라인에서 이탈된 거래처가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}